<template>
    <b-modal :visible="value" @change="$emit(`input`, $event)" dialog-class="e-browser-export-dialog fullscreen"
             content-class="e-browser-export-content" body-class="e-browser-export-body"
             :title="'Export items|Exporter les éléments'|tr" @hidden="onHidden">
        <div class="export-workbench">
            <aside class="control-rail">
                <b-form-group :label="'Format|Format'|tr">
                    <b-btn-group class="w-100">
                        <b-btn v-for="formatOption in formatOptions" :key="formatOption.value"
                               :variant="options.format === formatOption.value ? `primary` : `simple`"
                               @click="options.format = formatOption.value">
                            <i :class="formatOption.icon" class="mr-1"/>
                            <span>{{ formatOption.label }}</span>
                        </b-btn>
                    </b-btn-group>
                </b-form-group>

                <b-form-group class="columns-section">
                    <template #label>
                        <span v-tr>Columns|Colonnes</span>
                        <span class="columns-count float-right">{{ visibleColumns.length }}/{{ columns.length }}</span>
                    </template>
                    <div class="columns-list">
                        <b-checkbox v-for="column in columns" :key="column.key" :checked="column.visible" @change="toggleColumn(column)"
                                    class="column-row" :class="{ 'column-hidden': !column.visible }">
                            <span class="column-row-label">{{ tr(column.label) }}</span>
                            <i v-if="column.renamed" class="fas fa-pen column-renamed-icon" v-b-tooltip
                               :title="'Renamed column|Colonne renommée'|tr"/>
                        </b-checkbox>
                    </div>
                    <b-btn variant="link" size="sm" class="reset-columns-button px-0" @click="resetColumns">
                        <i class="fas fa-undo mr-1"/>
                        <span v-tr>Reset columns|Réinitialiser les colonnes</span>
                    </b-btn>
                </b-form-group>

                <b-form-group :label="'Options|Options'|tr" class="mb-0">
                    <b-checkbox v-if="selectedItems.length > 0" v-model="exportOnlySelection">
                        {{ tr(`Export only selection (${selectedItems.length})|Exporter uniquement la sélection (${selectedItems.length})`) }}
                    </b-checkbox>
                    <b-checkbox v-if="isPdfFormat && hasImageFields" v-model="options.includeImages">
                        <span v-tr>Include images|Inclure les images</span>
                    </b-checkbox>
                    <b-checkbox v-if="archiveEnabled" v-model="options.includeArchived">
                        <span v-tr>Include archived items|Inclure les éléments archivés</span>
                    </b-checkbox>
                    <b-checkbox v-if="trashEnabled" v-model="options.includeDeleted">
                        <span v-tr>Include deleted items|Inclure les éléments supprimés</span>
                    </b-checkbox>
                    <b-checkbox v-if="allowRaw" v-model="options.raw">
                        <span v-tr>Raw export (without formatting)|Export brut (sans formatage)</span>
                    </b-checkbox>
                </b-form-group>
            </aside>

            <div class="preview-zone">
                <div class="preview-toolbar">
                    <div class="preview-search">
                        <b-input v-model="search" maxlength="50" autocomplete="off"
                                 :placeholder="'Search to filter items|Rechercher pour filtrer les éléments'|tr"/>
                        <i v-show="!search" class="fas fa-search"/>
                        <i v-show="search" class="fas fa-times clear-search" @click="search = ``"/>
                    </div>
                    <span class="preview-count">{{ itemCountText }}</span>
                </div>

                <div class="preview-backdrop">
                    <div class="sheet" :class="{ 'paper': isPdfFormat }">
                        <div v-if="isPdfFormat" class="sheet-title">
                            <input v-model="pdfTitle" :placeholder="'Untitled export|Export sans titre'|tr"
                                   maxlength="80" :title="'This title is printed at the top of the PDF|Ce titre est imprimé en haut du PDF'|tr">
                            <i class="fas fa-pen"/>
                        </div>

                        <table v-if="visibleColumns.length > 0 && previewRows.length > 0" ref="previewTable" class="preview-table"
                               :class="{ 'fixed-layout': hasCustomWidths, 'fixed-rows': rowHeightPixels !== null }"
                               :style="rowHeightPixels !== null ? { '--export-row-height': rowHeightPixels + `px` } : null">
                            <colgroup>
                                <col v-for="column in visibleColumns" :key="column.key"
                                     :style="column.widthPixels ? { width: column.widthPixels + `px` } : null">
                            </colgroup>
                            <thead>
                                <tr>
                                    <th v-for="(column, columnIndex) in visibleColumns" :key="column.key"
                                        :draggable="renamingKey !== column.key && resizingIndex === null"
                                        :class="headerCellClass(column, columnIndex)"
                                        @dragstart="onColumnDragStart(column, $event)"
                                        @dragover.prevent="onColumnDragOver(column, $event)"
                                        @dragleave="onColumnDragLeave(column, $event)"
                                        @drop.prevent="onColumnDrop(column, $event)"
                                        @dragend="onColumnDragEnd">
                                        <div v-if="renamingKey === column.key" class="header-rename">
                                            <input ref="renameInput" v-model="renameDraft" maxlength="60"
                                                   @keydown.enter.prevent="commitRename" @keydown.esc.prevent="cancelRename"
                                                   @blur="commitRename" @click.stop @dblclick.stop>
                                        </div>
                                        <div v-else class="header-content" @dblclick.stop="startRename(column)">
                                            <span class="header-label">{{ tr(column.label) }}</span>
                                            <span class="sort-indicator" @click.stop="changeSort(column)" @dblclick.stop
                                                  v-b-tooltip :title="'Sort|Trier'|tr">
                                                <i v-if="sortState.key === column.key && sortState.order === `asc`" class="fas fa-caret-up active"/>
                                                <i v-else-if="sortState.key === column.key && sortState.order === `desc`" class="fas fa-caret-down active"/>
                                                <i v-else class="fas fa-sort"/>
                                            </span>
                                        </div>
                                        <span class="column-resize-handle" draggable="false"
                                              @mousedown.prevent.stop="startResize(columnIndex, $event)"
                                              @click.stop @dragstart.prevent/>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(row, rowIndex) in previewRows" :key="rowIndex">
                                    <td v-for="(column, columnIndex) in visibleColumns" :key="column.key">
                                        <img v-if="row.images[column.key]" :src="row.images[column.key]" class="cell-image" alt="">
                                        <span v-if="row.values[column.key]" class="cell-text">{{ row.values[column.key] }}</span>
                                        <span class="row-resize-handle" @mousedown.prevent.stop="startRowResize(rowIndex, $event)"/>
                                        <span class="column-resize-handle" draggable="false"
                                              @mousedown.prevent.stop="startResize(columnIndex, $event)" @click.stop/>
                                    </td>
                                </tr>
                            </tbody>
                        </table>

                        <div v-else class="preview-empty">
                            <i class="fas fa-table"/>
                            <p v-if="visibleColumns.length === 0" v-tr>
                                All columns are hidden. Check a column in the panel on the left to add it back.|Toutes les colonnes sont masquées. Cochez une colonne dans le panneau de gauche pour la rétablir.
                            </p>
                            <p v-else-if="search" v-tr>
                                No items match your search.|Aucun élément ne correspond à votre recherche.
                            </p>
                            <p v-else v-tr>
                                Nothing to export.|Il n'y a aucun élément à exporter.
                            </p>
                        </div>
                    </div>

                    <p v-if="itemsForExport.length > previewLimit" class="preview-note">
                        {{ tr(`Preview shows the first ${previewLimit} items. All ${itemsForExport.length} items will be exported.|L'aperçu montre les ${previewLimit} premiers éléments. Les ${itemsForExport.length} éléments seront exportés.`) }}
                    </p>
                </div>

                <div class="hint-bar">
                    <span><i class="fas fa-arrows-alt-h"/> <span v-tr>Drag a header to reorder|Glissez un en-tête pour réorganiser</span></span>
                    <span><i class="fas fa-text-width"/> <span v-tr>Drag between two columns or two rows to resize|Glissez entre deux colonnes ou deux rangées pour redimensionner</span></span>
                    <span><i class="fas fa-i-cursor"/> <span v-tr>Double-click a header to rename|Double-cliquez sur un en-tête pour renommer</span></span>
                    <span><i class="fas fa-sort"/> <span v-tr>Click the sort icon to sort|Cliquez sur l'icône de tri pour trier</span></span>
                </div>
            </div>
        </div>

        <template #modal-footer>
            <b-btn variant="light" @click="close">
                <span v-tr>Cancel|Annuler</span>
            </b-btn>
            <b-btn variant="primary" :disabled="!canExport" @click="exportItems">
                <i class="fas fa-file-export mr-1"/>
                {{ exportButtonText }}
            </b-btn>
        </template>
    </b-modal>
</template>

<script>
import BrowserHelper from "../helpers/BrowserHelper.js";
import {getNestedValue, download} from "../helpers/utils.js";
import {jsPDF as JsPDF} from "jspdf";
import autoTable from "jspdf-autotable";

// e-browser-export-modal
export default {
    name: `e-browser-export-modal`,
    props: {
        value: {type: Boolean, default: false},
        items: {type: Array, default: () => []},
        fields: {type: Array, default: () => []},
        title: {type: String, default: null},
        translate: {type: Boolean, default: false},
        icon: {type: [String, Function], default: null},
        thumbnail: {type: [String, Function], default: null},
        thumbnailExportField: {type: Object, default: null},
        archiveEnabled: {type: Boolean, default: false},
        trashEnabled: {type: Boolean, default: false},
        searchableKeys: {type: Array, default: () => [`name`, `description`]},
        initialSearch: {type: String, default: ``},
        allowRaw: {type: Boolean, default: false},
        selectedItems: {type: Array, default: () => []}
    },
    data() {
        return {
            options: {
                format: `csv`,
                raw: false,
                includeArchived: false,
                includeDeleted: false,
                includeImages: true
            },
            columns: [],
            pdfTitle: ``,
            search: ``,
            exportOnlySelection: false,
            sortState: {
                key: null,
                order: null
            },
            previewLimit: 50,
            renamingKey: null,
            renameDraft: ``,
            draggingKey: null,
            dropTarget: null,
            resizingIndex: null,
            resizeStartX: 0,
            resizeStartWidth: 0,
            resizeStartNeighborWidth: null,
            rowHeightPixels: null,
            rowResizingIndex: null,
            rowResizeStartY: 0,
            rowResizeStartHeight: 0,
            formatOptions: [
                {value: `csv`, label: `CSV`, icon: `fas fa-file-csv`},
                {value: `json`, label: `JSON`, icon: `fas fa-code`},
                {value: `pdf`, label: `PDF`, icon: `fas fa-file-pdf`}
            ],
            optionsLoaded: false
        }
    },
    computed: {
        isPdfFormat() {
            return this.options.format === `pdf`;
        },
        visibleColumns() {
            return this.columns.filter(column => column.visible);
        },
        hasCustomWidths() {
            return this.visibleColumns.some(column => column.widthPixels);
        },
        hasItemIcon() {
            return Boolean(this.thumbnail || this.icon);
        },
        hasImageFields() {
            return this.fields.some(field => field.exportImage) || this.hasItemIcon;
        },
        itemsForExport() {
            let items;
            if (this.exportOnlySelection && this.selectedItems.length > 0) {
                items = [...this.selectedItems];
            } else {
                items = this.items ? [...this.items] : [];
            }

            if (!this.options.includeArchived) {
                items = items.filter(item => !item.archived);
            }
            if (!this.options.includeDeleted) {
                items = items.filter(item => !item.deleted);
            }

            items = this.applySearchFilter(items);

            if (this.sortState.key && this.sortState.order) {
                const sortColumn = this.columns.find(column => column.key === this.sortState.key);
                const sortType = sortColumn && sortColumn.field ? sortColumn.field.type : null;
                const key = this.sortState.key;

                items.sort((itemA, itemB) => {
                    const item1 = this.sortState.order === `desc` ? itemB : itemA;
                    const item2 = this.sortState.order === `desc` ? itemA : itemB;

                    const value1 = getNestedValue(item1, key);
                    const value2 = getNestedValue(item2, key);

                    if (value1 === undefined || value1 === null) {
                        return -1;
                    } else if (value2 === undefined || value2 === null) {
                        return 1;
                    }

                    if (sortType === `string` || (!sortType && key === `name`))
                        return String(value1 || ``).localeCompare(String(value2 || ``));
                    else if (sortType === `number`)
                        return value1 - value2;
                    else if (sortType === `translation`)
                        return this.tr(value1).localeCompare(this.tr(value2));
                    else if (sortType === `date`)
                        return new Date(value1) - new Date(value2);
                    else
                        return value1 > value2 ? 1 : -1;
                });
            }

            return items;
        },
        previewRows() {
            const rows = [];
            for (const item of this.itemsForExport.slice(0, this.previewLimit)) {
                const values = {};
                const images = {};

                for (const column of this.visibleColumns) {
                    const showImage = this.isPdfFormat && this.options.includeImages && column.field && column.field.exportImage;

                    if (showImage) {
                        const imageSource = this.resolveImageSource(column.field, item);
                        if (imageSource) {
                            images[column.key] = imageSource;
                        }
                    }

                    if (showImage && this.isImageOnlyColumn(column)) {
                        values[column.key] = ``;
                        continue;
                    }

                    values[column.key] = this.getExportValue(item, column);
                }

                rows.push({values, images});
            }
            return rows;
        },
        canExport() {
            return this.itemsForExport.length > 0 && this.visibleColumns.length > 0;
        },
        itemCountText() {
            const shownCount = this.itemsForExport.length;
            const totalCount = this.items ? this.items.length : 0;
            if (shownCount === totalCount) {
                return this.tr(`${totalCount} items|${totalCount} éléments`);
            }
            return this.tr(`${shownCount} of ${totalCount} items|${shownCount} sur ${totalCount} éléments`);
        },
        exportButtonText() {
            const count = this.itemsForExport.length;
            return `${this.tr(`Export ${count} items|Exporter ${count} éléments`)} · ${this.options.format.toUpperCase()}`;
        },
        layoutStorageKey() {
            return `eBrowserExportLayout:${this.title || `default`}`;
        }
    },
    created() {
        this.initialize();
    },
    beforeDestroy() {
        this.detachResizeListeners();
        this.stopRowResize();
    },
    methods: {
        initialize() {
            this.loadOptions();
            this.buildColumns();
            this.search = this.initialSearch || ``;
            this.exportOnlySelection = this.selectedItems.length > 0;
            this.optionsLoaded = true;
        },
        loadOptions() {
            const storedOptions = localStorage.getItem(`eBrowserExportOptions`);
            if (storedOptions) {
                try {
                    this.options = {...this.options, ...JSON.parse(storedOptions)};
                } catch (error) {
                    console.error(error);
                }
            }
            if (this.options.includeImages === undefined) {
                this.options.includeImages = this.hasImageFields;
            }
        },
        saveOptions() {
            localStorage.setItem(`eBrowserExportOptions`, JSON.stringify(this.options));
        },
        buildColumns() {
            let columns;
            if (this.options.raw) {
                const firstItem = this.items && this.items.length > 0 ? this.items[0] : null;
                columns = firstItem
                    ? Object.keys(firstItem).map(key => this.createColumn({key: key, label: key}))
                    : [];
            } else {
                columns = this.fields.map(field => this.createColumn(field));
                if (this.hasItemIcon) {
                    if (this.thumbnailExportField) {
                        columns.unshift(this.createColumn(this.thumbnailExportField));
                    } else {
                        columns.unshift(this.createColumn({
                            key: `__icon__`,
                            label: `Icon|Icône`,
                            imageOnly: true,
                            exportImage: item => this.resolveItemIconSource(item)
                        }));
                    }
                }
                columns = this.applyStoredLayout(columns);
                if (this.thumbnailExportField && this.thumbnailExportField.first) {
                    const thumbnailColumnIndex = columns.findIndex(column => column.key === this.thumbnailExportField.key);
                    if (thumbnailColumnIndex > 0) {
                        columns.unshift(columns.splice(thumbnailColumnIndex, 1)[0]);
                    }
                }
            }
            this.columns = columns;
        },
        resolveItemIconSource(item) {
            if (this.thumbnail) {
                const thumbnailValue = typeof this.thumbnail === `function`
                    ? this.thumbnail(item)
                    : getNestedValue(item, this.thumbnail);
                const thumbnailSource = thumbnailValue && thumbnailValue.url ? thumbnailValue.url : thumbnailValue;
                if (thumbnailSource) {
                    return thumbnailSource;
                }
            }
            if (this.icon) {
                if (typeof this.icon === `function`) {
                    return this.icon(item);
                }
                if (this.icon.indexOf(`.png`) > -1) {
                    return this.icon;
                }
                return this.getPublicIcon(this.icon);
            }
            return null;
        },
        isImageOnlyColumn(column) {
            return Boolean(column.field && (column.field.imageOnly ||
                (column.field.exportImage && column.field.exportImage === column.key)));
        },
        createColumn(field) {
            return {
                key: field.key,
                label: field.label || field.key,
                renamed: false,
                visible: !field.hidden,
                widthPixels: null,
                field: field
            };
        },
        applyStoredLayout(columns) {
            const storedLayout = localStorage.getItem(this.layoutStorageKey);
            let layout = null;
            if (storedLayout) {
                try {
                    layout = JSON.parse(storedLayout);
                } catch (error) {
                    console.error(error);
                }
            }

            if (layout && layout.pdfTitle !== undefined) {
                this.pdfTitle = layout.pdfTitle;
            } else {
                this.pdfTitle = this.title ? this.tr(this.title) : ``;
            }
            this.rowHeightPixels = layout && layout.rowHeightPixels ? layout.rowHeightPixels : null;

            if (!layout || !Array.isArray(layout.columns)) {
                return columns;
            }

            const orderedColumns = [];
            for (const storedColumn of layout.columns) {
                const matchingColumn = columns.find(column => column.key === storedColumn.key);
                if (matchingColumn) {
                    if (storedColumn.renamed && storedColumn.label) {
                        matchingColumn.label = storedColumn.label;
                        matchingColumn.renamed = true;
                    }
                    matchingColumn.visible = storedColumn.visible !== false;
                    matchingColumn.widthPixels = storedColumn.widthPixels || null;
                    orderedColumns.push(matchingColumn);
                }
            }
            // Columns unknown to the stored layout (e.g. newly added fields or the
            // icon column) keep their natural position instead of going last
            for (let columnIndex = 0; columnIndex < columns.length; columnIndex++) {
                const column = columns[columnIndex];
                if (!orderedColumns.includes(column)) {
                    orderedColumns.splice(Math.min(columnIndex, orderedColumns.length), 0, column);
                }
            }
            return orderedColumns;
        },
        saveLayout() {
            if (this.options.raw) {
                return;
            }
            localStorage.setItem(this.layoutStorageKey, JSON.stringify({
                pdfTitle: this.pdfTitle,
                rowHeightPixels: this.rowHeightPixels,
                columns: this.columns.map(column => ({
                    key: column.key,
                    label: column.label,
                    renamed: column.renamed,
                    visible: column.visible,
                    widthPixels: column.widthPixels
                }))
            }));
        },
        resetColumns() {
            localStorage.removeItem(this.layoutStorageKey);
            this.pdfTitle = this.title ? this.tr(this.title) : ``;
            this.sortState = {key: null, order: null};
            this.rowHeightPixels = null;
            this.buildColumns();
        },
        toggleColumn(column) {
            column.visible = !column.visible;

            // In fixed layout every visible column has an explicit width, so a column
            // revealed without one would be squeezed to zero — give it a fair width
            if (column.visible && !column.widthPixels && this.hasCustomWidths) {
                const customWidths = this.visibleColumns
                    .filter(visibleColumn => visibleColumn !== column && visibleColumn.widthPixels)
                    .map(visibleColumn => visibleColumn.widthPixels);
                const averageWidth = customWidths.length > 0
                    ? customWidths.reduce((sum, width) => sum + width, 0) / customWidths.length
                    : 120;
                column.widthPixels = Math.max(80, Math.round(averageWidth));
            }
        },
        changeSort(column) {
            if (this.sortState.key !== column.key) {
                this.sortState = {key: column.key, order: `asc`};
            } else if (this.sortState.order === `asc`) {
                this.sortState = {key: column.key, order: `desc`};
            } else {
                this.sortState = {key: null, order: null};
            }
        },
        startRename(column) {
            this.renamingKey = column.key;
            this.renameDraft = this.tr(column.label);
            this.$nextTick(() => {
                if (this.$refs.renameInput && this.$refs.renameInput[0]) {
                    this.$refs.renameInput[0].focus();
                    this.$refs.renameInput[0].select();
                }
            });
        },
        commitRename() {
            if (this.renamingKey === null) {
                return;
            }
            const column = this.columns.find(currentColumn => currentColumn.key === this.renamingKey);
            const newLabel = this.renameDraft.trim();
            if (column && newLabel && newLabel !== this.tr(column.label)) {
                column.label = newLabel;
                column.renamed = true;
            }
            this.renamingKey = null;
        },
        cancelRename() {
            this.renamingKey = null;
        },
        headerCellClass(column, columnIndex) {
            return [
                this.columnDropClass(column),
                {
                    'drag-source': this.draggingKey === column.key,
                    'resizing': this.resizingIndex === columnIndex
                }
            ];
        },
        columnDropClass(column) {
            if (!this.dropTarget || this.dropTarget.key !== column.key || this.draggingKey === column.key) {
                return null;
            }
            return this.dropTarget.after ? `drop-after` : `drop-before`;
        },
        onColumnDragStart(column, event) {
            this.draggingKey = column.key;
            event.dataTransfer.effectAllowed = `move`;
            event.dataTransfer.setData(`text/plain`, column.key);
        },
        onColumnDragOver(column, event) {
            if (!this.draggingKey || this.draggingKey === column.key) {
                if (this.dropTarget) {
                    this.dropTarget = null;
                }
                return;
            }
            const boundingRect = event.currentTarget.getBoundingClientRect();
            const after = event.clientX > boundingRect.left + boundingRect.width / 2;
            // Only touch the reactive state when the drop position actually changes,
            // otherwise every dragover event re-renders the whole preview
            if (!this.dropTarget || this.dropTarget.key !== column.key || this.dropTarget.after !== after) {
                this.dropTarget = {key: column.key, after: after};
            }
        },
        onColumnDragLeave(column, event) {
            if (event.currentTarget.contains(event.relatedTarget)) {
                return;
            }
            if (this.dropTarget && this.dropTarget.key === column.key) {
                this.dropTarget = null;
            }
        },
        onColumnDrop(column, event) {
            if (!this.draggingKey || this.draggingKey === column.key) {
                this.onColumnDragEnd();
                return;
            }

            const boundingRect = event.currentTarget.getBoundingClientRect();
            const after = event.clientX > boundingRect.left + boundingRect.width / 2;

            const columns = [...this.columns];
            const sourceIndex = columns.findIndex(currentColumn => currentColumn.key === this.draggingKey);
            const [movedColumn] = columns.splice(sourceIndex, 1);
            let targetIndex = columns.findIndex(currentColumn => currentColumn.key === column.key);
            if (after) {
                targetIndex++;
            }
            columns.splice(targetIndex, 0, movedColumn);
            this.columns = columns;

            this.onColumnDragEnd();
        },
        onColumnDragEnd() {
            this.draggingKey = null;
            this.dropTarget = null;
        },
        startResize(columnIndex, event) {
            if (!this.hasCustomWidths && this.$refs.previewTable) {
                const headerCells = this.$refs.previewTable.querySelectorAll(`thead th`);
                this.visibleColumns.forEach((column, index) => {
                    if (headerCells[index]) {
                        column.widthPixels = headerCells[index].offsetWidth;
                    }
                });
            }

            this.resizingIndex = columnIndex;
            this.resizeStartX = event.clientX;
            this.resizeStartWidth = this.visibleColumns[columnIndex].widthPixels;
            const neighborColumn = this.visibleColumns[columnIndex + 1];
            this.resizeStartNeighborWidth = neighborColumn ? neighborColumn.widthPixels : null;

            document.body.classList.add(`e-browser-column-resizing`);
            document.addEventListener(`mousemove`, this.onResizeMove);
            document.addEventListener(`mouseup`, this.stopResize);
        },
        onResizeMove(event) {
            if (this.resizingIndex === null) {
                return;
            }
            const minimumWidth = 48;
            const column = this.visibleColumns[this.resizingIndex];
            const neighborColumn = this.visibleColumns[this.resizingIndex + 1];
            let deltaX = event.clientX - this.resizeStartX;

            if (neighborColumn && this.resizeStartNeighborWidth !== null) {
                // Shared-edge resize: what the left column gains, the right column loses
                deltaX = Math.max(minimumWidth - this.resizeStartWidth, Math.min(deltaX, this.resizeStartNeighborWidth - minimumWidth));
                column.widthPixels = Math.round(this.resizeStartWidth + deltaX);
                neighborColumn.widthPixels = Math.round(this.resizeStartNeighborWidth - deltaX);
            } else {
                column.widthPixels = Math.max(minimumWidth, Math.round(this.resizeStartWidth + deltaX));
            }
        },
        stopResize() {
            this.resizingIndex = null;
            this.detachResizeListeners();
        },
        startRowResize(rowIndex, event) {
            if (this.rowHeightPixels === null && this.$refs.previewTable) {
                const firstBodyRow = this.$refs.previewTable.querySelector(`tbody tr`);
                if (!firstBodyRow) {
                    return;
                }
                this.rowHeightPixels = firstBodyRow.offsetHeight;
            }

            this.rowResizingIndex = rowIndex;
            this.rowResizeStartY = event.clientY;
            this.rowResizeStartHeight = this.rowHeightPixels;

            document.body.classList.add(`e-browser-row-resizing`);
            document.addEventListener(`mousemove`, this.onRowResizeMove);
            document.addEventListener(`mouseup`, this.stopRowResize);
        },
        onRowResizeMove(event) {
            if (this.rowResizingIndex === null) {
                return;
            }
            // All rows share the same height, so the height change is spread over
            // every row above the dragged edge to keep that edge under the cursor
            const deltaY = (event.clientY - this.rowResizeStartY) / (this.rowResizingIndex + 1);
            this.rowHeightPixels = Math.max(22, Math.round(this.rowResizeStartHeight + deltaY));
        },
        stopRowResize() {
            this.rowResizingIndex = null;
            document.body.classList.remove(`e-browser-row-resizing`);
            document.removeEventListener(`mousemove`, this.onRowResizeMove);
            document.removeEventListener(`mouseup`, this.stopRowResize);
        },
        detachResizeListeners() {
            document.body.classList.remove(`e-browser-column-resizing`);
            document.removeEventListener(`mousemove`, this.onResizeMove);
            document.removeEventListener(`mouseup`, this.stopResize);
        },
        applySearchFilter(items) {
            if (!this.search) {
                return items;
            }

            let searchExpression;
            try {
                searchExpression = new RegExp(this.search, `i`);
            } catch (error) {
                searchExpression = new RegExp(this.search.replace(/[.*+?^${}()|[\]\\]/g, `\\$&`), `i`);
            }

            const matchingItems = [];
            for (const item of items) {
                let added = false;
                for (const key of this.searchableKeys) {
                    const nestedValue = getNestedValue(item, key);
                    if (nestedValue !== undefined) {
                        if (typeof nestedValue === `object`) {
                            for (const nestedKey in nestedValue) {
                                if (searchExpression.test(nestedValue[nestedKey])) {
                                    matchingItems.push(item);
                                    added = true;
                                    break;
                                }
                            }
                        } else if (searchExpression.test(nestedValue)) {
                            added = true;
                            matchingItems.push(item);
                        }
                    }
                    if (added) {
                        break;
                    }
                }
            }
            return matchingItems;
        },
        getExportValue(item, column) {
            if (column.field && column.field.imageOnly) {
                return this.resolveImageSource(column.field, item) || ``;
            }
            const field = column.field && !this.options.raw ? column.field : {key: column.key};
            let nestedValue = BrowserHelper.getFormattedValue(item, field, `export`);

            if (nestedValue === undefined || nestedValue === null) {
                return ``;
            }
            if (column.key === `name` || this.translate) {
                nestedValue = this.tr(nestedValue);
            }
            if (typeof nestedValue === `object`) {
                return JSON.stringify(nestedValue);
            }
            return String(nestedValue);
        },
        resolveImageSource(field, item) {
            const imageValue = typeof field.exportImage === `function`
                ? field.exportImage(item)
                : getNestedValue(item, field.exportImage);
            return imageValue && imageValue.url ? imageValue.url : imageValue;
        },
        resolveImageLink(field, item) {
            if (!field.exportLink) {
                return null;
            }
            const linkValue = typeof field.exportLink === `function`
                ? field.exportLink(item)
                : getNestedValue(item, field.exportLink);
            return linkValue && linkValue.url ? linkValue.url : linkValue;
        },
        exportFileName() {
            if (this.isPdfFormat && this.pdfTitle) {
                return this.pdfTitle;
            }
            return this.title ? this.tr(this.title) : `Export`;
        },
        showExportLoading() {
            const spinner = document.createElement(`i`);
            spinner.className = `fas fa-spinner fa-spin`;
            spinner.style.fontSize = `34px`;
            spinner.style.color = `#168ec5`;
            swal({
                title: this.tr(`Exporting...|Exportation...`),
                text: this.tr(`Preparing your file, this can take a few seconds|Préparation de votre fichier, cela peut prendre quelques secondes`),
                content: spinner,
                buttons: false,
                closeOnClickOutside: false,
                closeOnEsc: false
            });
        },
        async exportItems() {
            if (!this.canExport) {
                return;
            }

            this.commitRename();
            this.saveOptions();
            this.saveLayout();

            const itemsToExport = this.itemsForExport;

            this.showExportLoading();
            // Give the browser a frame to paint the loading alert before the heavy work starts
            await new Promise(resolve => setTimeout(resolve, 50));

            try {
                await this.loadMissingExportImages(itemsToExport);
                switch (this.options.format) {
                    case `csv`:
                        this.exportCSV(itemsToExport);
                        break;
                    case `pdf`:
                        await this.exportPDF(itemsToExport);
                        break;
                    case `json`:
                        this.exportJSON(itemsToExport);
                        break;
                }
            } finally {
                swal.close();
            }

            this.close();
        },
        async loadMissingExportImages(itemsToExport) {
            for (const column of this.visibleColumns) {
                if (!column.field || !column.field.loadExportImage) {
                    continue;
                }
                for (const item of itemsToExport) {
                    if (this.resolveImageSource(column.field, item)) {
                        continue;
                    }
                    try {
                        await column.field.loadExportImage(item);
                    } catch (error) {
                        console.error(error);
                    }
                }
            }
        },
        exportCSV(itemsToExport) {
            const separator = `;`;

            let csv = `SEP=${separator}\n`;
            for (const column of this.visibleColumns) {
                csv += this.tr(column.label) + separator;
            }

            for (const item of itemsToExport) {
                csv += `\n`;
                for (const column of this.visibleColumns) {
                    csv += `"${this.getExportValue(item, column)}"` + separator;
                }
            }

            download(`${this.exportFileName()}.csv`, csv);
        },
        exportJSON(itemsToExport) {
            if (this.options.raw) {
                download(`${this.exportFileName()}.json`, JSON.stringify(itemsToExport, null, 2));
                return;
            }

            const exportedItems = [];
            for (const item of itemsToExport) {
                const exportedItem = {};
                for (const column of this.visibleColumns) {
                    exportedItem[this.tr(column.label)] = this.getExportValue(item, column);
                }
                exportedItems.push(exportedItem);
            }

            download(`${this.exportFileName()}.json`, JSON.stringify(exportedItems, null, 2));
        },
        async exportPDF(itemsToExport) {
            const exportColumns = this.visibleColumns;
            const imageColumns = this.options.includeImages
                ? exportColumns.filter(column => column.field && column.field.exportImage)
                : [];

            const columns = exportColumns.map(column => ({
                header: this.tr(column.label),
                dataKey: column.key
            }));

            const cellImages = {};
            const cellLinks = {};
            for (let index = 0; index < itemsToExport.length; index++) {
                for (const column of imageColumns) {
                    const imageSource = this.resolveImageSource(column.field, itemsToExport[index]);
                    if (!imageSource) {
                        continue;
                    }
                    const cellImage = await this.loadImageAsDataUrl(imageSource);
                    if (cellImage) {
                        cellImages[`${index}-${column.key}`] = cellImage;
                        const imageLink = this.resolveImageLink(column.field, itemsToExport[index]);
                        if (imageLink) {
                            cellLinks[`${index}-${column.key}`] = imageLink;
                        }
                    }
                }
            }

            const rows = itemsToExport.map(item => {
                const row = {};
                for (const column of exportColumns) {
                    if (this.options.includeImages && this.isImageOnlyColumn(column)) {
                        row[column.key] = ``;
                        continue;
                    }
                    row[column.key] = this.getExportValue(item, column);
                }
                return row;
            });

            const pdf = new JsPDF({
                orientation: `landscape`,
                unit: `mm`,
                format: `a4`
            });

            const pageMargin = 10;
            if (this.pdfTitle) {
                pdf.setFontSize(14);
                pdf.text(this.pdfTitle, 14, 15);
            }

            const usableWidth = 297 - pageMargin * 2;
            const columnStyles = {};
            let pixelsPerMillimeter = null;

            // Mirror the preview's actual rendered column widths so the PDF proportions
            // always match what the user sees, resized or not
            let renderedWidths = null;
            if (this.$refs.previewTable) {
                const headerCells = this.$refs.previewTable.querySelectorAll(`thead th`);
                if (headerCells.length === exportColumns.length) {
                    renderedWidths = Array.from(headerCells).map(headerCell => headerCell.offsetWidth);
                }
            }

            if (renderedWidths) {
                const totalPixels = renderedWidths.reduce((sum, width) => sum + width, 0);
                pixelsPerMillimeter = totalPixels / usableWidth;
                exportColumns.forEach((column, columnIndex) => {
                    columnStyles[column.key] = {cellWidth: (renderedWidths[columnIndex] / totalPixels) * usableWidth};
                });
            } else if (exportColumns.every(column => column.widthPixels)) {
                const totalPixels = exportColumns.reduce((sum, column) => sum + column.widthPixels, 0);
                pixelsPerMillimeter = totalPixels / usableWidth;
                for (const column of exportColumns) {
                    columnStyles[column.key] = {cellWidth: (column.widthPixels / totalPixels) * usableWidth};
                }
            }

            let rowHeightMillimeters = null;
            if (this.rowHeightPixels && pixelsPerMillimeter) {
                rowHeightMillimeters = this.rowHeightPixels / pixelsPerMillimeter;
            }

            const imageCellHeight = Math.max(20, rowHeightMillimeters || 0);
            const imageCellWidth = Math.max(32, imageCellHeight * 1.6);

            // Fallback when the preview could not be measured: autoTable sizes image-only
            // columns from their (empty) text — reserve a width that follows the row height
            if (this.options.includeImages && Object.keys(columnStyles).length === 0) {
                for (const column of exportColumns) {
                    if (this.isImageOnlyColumn(column)) {
                        columnStyles[column.key] = {cellWidth: Math.min(imageCellWidth, usableWidth / exportColumns.length)};
                    }
                }
            }

            const bodyStyles = {};
            if (rowHeightMillimeters) {
                bodyStyles.minCellHeight = rowHeightMillimeters;
            }

            autoTable(pdf, {
                startY: this.pdfTitle ? 22 : 12,
                columns,
                body: rows,
                theme: `grid`,
                rowPageBreak: `avoid`,
                styles: {
                    fontSize: 8,
                    cellPadding: 2,
                    overflow: `linebreak`,
                    valign: `middle`,
                    textColor: 0
                },
                bodyStyles,
                headStyles: {
                    fontStyle: `bold`,
                    fillColor: `#d6f1da`,
                    textColor: 0
                },
                columnStyles,
                margin: {
                    left: pageMargin,
                    right: pageMargin
                },
                didParseCell: data => {
                    if (data.section !== `body` || !cellImages[`${data.row.index}-${data.column.dataKey}`]) {
                        return;
                    }
                    data.cell.styles.minCellHeight = Math.max(imageCellHeight, data.cell.styles.minCellHeight || 0);
                    const column = exportColumns.find(currentColumn => currentColumn.key === data.column.dataKey);
                    if (!this.isImageOnlyColumn(column)) {
                        data.cell.styles.cellPadding = {top: 2, right: 2, bottom: 2, left: imageCellWidth + 3};
                    }
                },
                didDrawCell: data => {
                    if (data.section !== `body`) {
                        return;
                    }
                    const cellImage = cellImages[`${data.row.index}-${data.column.dataKey}`];
                    if (!cellImage) {
                        return;
                    }
                    const column = exportColumns.find(currentColumn => currentColumn.key === data.column.dataKey);
                    // Image-only columns let the image fill the cell; mixed cells keep a cap so text stays readable
                    const imageWidthLimit = this.isImageOnlyColumn(column) ? data.cell.width - 2 : imageCellWidth;
                    const maxWidth = Math.min(data.cell.width - 2, imageWidthLimit);
                    const maxHeight = data.cell.height - 2;
                    const ratio = Math.min(maxWidth / cellImage.width, maxHeight / cellImage.height);
                    const width = cellImage.width * ratio;
                    const height = cellImage.height * ratio;
                    const imageX = data.cell.x + 1;
                    const imageY = data.cell.y + (data.cell.height - height) / 2;
                    pdf.addImage(cellImage.dataUrl, cellImage.format, imageX, imageY, width, height);
                    const imageLink = cellLinks[`${data.row.index}-${data.column.dataKey}`];
                    if (imageLink) {
                        pdf.link(imageX, imageY, width, height, {url: imageLink, target: `_blank`});
                    }
                }
            });

            pdf.save(`${this.exportFileName()}.pdf`);
        },
        loadImageAsDataUrl(source) {
            return new Promise(resolve => {
                const image = new Image();
                image.crossOrigin = `anonymous`;
                image.onload = () => {
                    const canvas = document.createElement(`canvas`);
                    canvas.width = image.naturalWidth;
                    canvas.height = image.naturalHeight;
                    canvas.getContext(`2d`).drawImage(image, 0, 0);
                    try {
                        resolve({
                            dataUrl: canvas.toDataURL(`image/png`),
                            format: `PNG`,
                            width: image.naturalWidth,
                            height: image.naturalHeight
                        });
                    } catch (error) {
                        console.error(error);
                        resolve(null);
                    }
                };
                image.onerror = () => resolve(null);
                image.src = source;
            });
        },
        close() {
            this.$emit(`input`, false);
        },
        onHidden() {
            this.commitRename();
            this.stopResize();
            this.stopRowResize();
            this.saveLayout();
            this.$emit(`input`, false);
        }
    },
    watch: {
        value(visible) {
            if (visible) {
                this.initialize();
            }
        },
        options: {
            deep: true,
            handler() {
                if (this.optionsLoaded) {
                    this.saveOptions();
                }
            }
        },
        'options.raw'() {
            this.buildColumns();
        }
    }
}
</script>

<style lang="scss">
.e-browser-export-dialog {
    // Paper-sheet preview tokens (the sheet mimics the printed output, so it stays light)
    --export-ink: #2c3338;
    --export-muted: #87919a;
    --export-accent: var(--color-primary);
    --export-hairline: #e3e7ea;
    // Modal chrome tokens, from the app theme
    --export-chrome-border: rgba(var(--contrast-rgb), 0.1);

    max-width: min(94vw, 1440px);
    width: min(94vw, 1440px);
    margin-left: auto;
    margin-right: auto;

    .e-browser-export-body {
        padding: 0;
        display: flex;
        flex-direction: column;
        height: min(74vh, 820px);
        overflow: hidden;
    }

    .export-workbench {
        display: flex;
        flex: 1;
        min-height: 0;
    }

    // -- Left control rail --------------------------------------------------

    .control-rail {
        width: 280px;
        flex-shrink: 0;
        border-right: 1px solid var(--export-chrome-border);
        overflow-y: auto;
        padding: 16px;
        display: flex;
        flex-direction: column;

        .form-group {
            display: flex;
            flex-direction: column;
            min-height: 0;

            legend, label {
                font-weight: 600;
            }
        }
    }

    .columns-section {
        flex: 1;
        min-height: 140px;

        > div {
            flex: 1;
            display: flex;
            flex-direction: column;
            min-height: 0;
        }
    }

    .columns-count {
        color: var(--text-low-contrast);
        font-weight: 500;
    }

    .columns-list {
        flex: 1;
        min-height: 0;
        overflow-y: auto;
        border: 1px solid var(--input-border-color);
        border-radius: var(--input-border-radius);
        background: var(--input-bg-color);
        padding: 6px 10px;
    }

    .column-row {
        padding-top: 2px;
        padding-bottom: 2px;

        .column-row-label {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        .column-renamed-icon {
            margin-left: 6px;
            font-size: 9px;
            color: var(--text-low-contrast);
        }

        &.column-hidden {
            .column-row-label {
                color: var(--text-low-contrast);
                text-decoration: line-through;
            }
        }
    }

    .reset-columns-button {
        align-self: flex-start;
        font-size: 12px;
    }

    // -- Preview zone -------------------------------------------------------

    .preview-zone {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
    }

    .preview-toolbar {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        padding: 10px 16px;
        border-bottom: 1px solid var(--export-chrome-border);
    }

    .preview-search {
        position: relative;
        width: 300px;
        max-width: 60%;

        input {
            padding-right: 30px;
        }

        .fas {
            position: absolute;
            top: 50%;
            right: 10px;
            transform: translateY(-50%);
            color: var(--text-low-contrast);
            font-size: 13px;

            &.clear-search {
                cursor: pointer;

                &:hover {
                    color: var(--text-color);
                }
            }
        }
    }

    .preview-count {
        font-size: 11px;
        font-weight: 600;
        letter-spacing: 0.5px;
        text-transform: uppercase;
        color: var(--text-low-contrast);
        white-space: nowrap;
    }

    .preview-backdrop {
        flex: 1;
        min-height: 0;
        overflow: auto;
        padding: 24px 28px;
        background: rgba(var(--blend-rgb), 0.25);
        box-shadow: inset 0 2px 6px rgba(0, 0, 0, 0.15);
    }

    .sheet {
        background: white;
        margin: 0 auto;
        max-width: 1160px;
        border-radius: 2px;
        border: 1px solid var(--export-hairline);
        overflow: hidden;

        &.paper {
            border: none;
            padding: 26px 30px 30px;
            box-shadow: 0 1px 2px rgba(20, 30, 40, 0.08), 0 12px 32px rgba(20, 30, 40, 0.16);
            font-family: Helvetica, Arial, sans-serif;
        }
    }

    .sheet-title {
        position: relative;
        margin-bottom: 14px;

        input {
            width: 100%;
            border: 1px dashed transparent;
            border-radius: 4px;
            background: transparent;
            font-family: Helvetica, Arial, sans-serif;
            font-size: 19px;
            font-weight: 600;
            color: var(--export-ink);
            padding: 3px 30px 3px 6px;
            margin-left: -6px;
            outline: none;
            transition: border-color 120ms ease, background 120ms ease;

            &::placeholder {
                color: rgba(135, 145, 154, 0.55);
                font-weight: 500;
            }

            &:hover {
                border-color: var(--export-hairline);
            }

            &:focus {
                border-color: var(--export-accent);
                background: rgba(22, 142, 197, 0.04);
            }
        }

        .fa-pen {
            position: absolute;
            top: 50%;
            right: 8px;
            transform: translateY(-50%);
            font-size: 11px;
            color: var(--export-muted);
            opacity: 0;
            pointer-events: none;
            transition: opacity 120ms ease;
        }

        &:hover .fa-pen {
            opacity: 1;
        }

        input:focus ~ .fa-pen {
            opacity: 0;
        }
    }

    // -- Preview table ------------------------------------------------------

    .preview-table {
        width: 100%;
        border-collapse: collapse;
        font-size: 11px;
        color: var(--export-ink);

        &.fixed-layout {
            table-layout: fixed;
        }

        th, td {
            border: 1px solid #d6dbdf;
            padding: 5px 8px;
            text-align: left;
            vertical-align: middle;
        }

        th {
            position: relative;
            background: #d6f1da;
            font-weight: 700;
            white-space: nowrap;
            cursor: grab;
            user-select: none;

            &.drag-source {
                opacity: 0.35;
            }

            &.resizing {
                cursor: col-resize;
            }
        }

        th.drop-before {
            box-shadow: inset 4px 0 0 var(--export-accent);
        }

        th.drop-after {
            box-shadow: inset -4px 0 0 var(--export-accent);
        }

        .header-content {
            display: flex;
            align-items: center;
            gap: 6px;
            min-width: 0;
        }

        .header-label {
            overflow: hidden;
            text-overflow: ellipsis;
        }

        .sort-indicator {
            margin-left: auto;
            flex-shrink: 0;
            cursor: pointer;
            padding: 2px 4px;
            margin-right: -4px;
            border-radius: 3px;

            &:hover {
                background: rgba(22, 142, 197, 0.12);
            }

            .fa-sort {
                color: var(--export-muted);
                opacity: 0;
                transition: opacity 120ms ease;
            }

            .active {
                color: var(--export-accent);
            }
        }

        th:hover .sort-indicator .fa-sort {
            opacity: 0.6;
        }

        .header-rename input {
            width: 100%;
            border: 1px solid var(--export-accent);
            border-radius: 3px;
            padding: 1px 5px;
            font-size: 11px;
            font-weight: 700;
            color: var(--export-ink);
            outline: none;
            box-shadow: 0 0 0 2px rgba(22, 142, 197, 0.15);
        }

        .column-resize-handle {
            position: absolute;
            top: 0;
            right: -5px;
            width: 9px;
            height: 100%;
            cursor: col-resize;
            z-index: 2;

            &:hover {
                background: linear-gradient(to right, transparent 3px, rgba(22, 142, 197, 0.55) 3px, rgba(22, 142, 197, 0.55) 6px, transparent 6px);
            }
        }

        td {
            position: relative;
            max-width: 260px;

            .cell-text {
                display: block;
                overflow: hidden;
                white-space: normal;
                word-break: break-word;
            }

            .cell-image {
                max-height: 30px;
                max-width: 100%;
                object-fit: contain;
                border-radius: 2px;
            }
        }

        .row-resize-handle {
            position: absolute;
            left: 0;
            right: 0;
            bottom: -5px;
            height: 9px;
            cursor: row-resize;
            z-index: 2;

            &:hover {
                background: linear-gradient(to bottom, transparent 3px, rgba(22, 142, 197, 0.55) 3px, rgba(22, 142, 197, 0.55) 6px, transparent 6px);
            }
        }

        &.fixed-rows {
            tbody tr {
                height: var(--export-row-height);
            }

            td {
                .cell-text {
                    max-height: calc(var(--export-row-height) - 11px);
                }

                .cell-image {
                    max-height: calc(var(--export-row-height) - 11px);
                }
            }
        }

        tbody tr:nth-child(even) td {
            background: #fafbfc;
        }
    }

    .preview-empty {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 10px;
        padding: 60px 30px;
        color: var(--export-muted);
        text-align: center;

        .fas {
            font-size: 28px;
            opacity: 0.4;
        }

        p {
            margin: 0;
            font-size: 13px;
            max-width: 360px;
        }
    }

    .preview-note {
        text-align: center;
        font-size: 11px;
        color: rgba(var(--contrast-rgb), 0.6);
        margin: 12px 0 0;
    }

    .hint-bar {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-wrap: wrap;
        gap: 4px 18px;
        padding: 7px 16px;
        border-top: 1px solid var(--export-chrome-border);
        font-size: 10.5px;
        color: var(--text-low-contrast);

        .fas {
            margin-right: 3px;
            font-size: 10px;
        }
    }

    @media (prefers-reduced-motion: reduce) {
        * {
            transition: none !important;
        }
    }
}

body.e-browser-column-resizing {
    cursor: col-resize !important;
    user-select: none;

    * {
        cursor: col-resize !important;
    }
}

body.e-browser-row-resizing {
    cursor: row-resize !important;
    user-select: none;

    * {
        cursor: row-resize !important;
    }
}

@media (max-width: 991px) {
    .e-browser-export-dialog {
        .export-workbench {
            flex-direction: column;
            overflow-y: auto;
        }

        .control-rail {
            width: 100%;
            border-right: none;
            border-bottom: 1px solid var(--export-chrome-border);
        }

        .columns-list {
            max-height: 180px;
        }

        .preview-zone {
            min-height: 380px;
        }
    }
}
</style>
